/* eslint-disable eqeqeq */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, History, X } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { ResponsiveDrawerForm } from "../../../components/ResponsiveDrawerForm";
import {
  API,
  defaultLimitPagination,
  fileUpload,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";
import {
  CaretDownIcon,
  CaretRightIcon,
  InfoCircledIcon,
  UpdateIcon,
} from "@radix-ui/react-icons";
import { formatFullAddressString, formatName, formatSubcontractorName } from "../../../lib/Misc";
import Confirmation from "../../../components/Confirmation";
import CustomTooltip from "../../../components/CustomTooltip";
import { useLocation } from "react-router-dom";
import moment from "moment";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../components/Pagination";
import { Badge } from "../../../components/ui/badge";
import Spin from "../../../components/Spin";

const NameUpdate = ({
  kycData,
  contactData,
  id,
  fetchData,
  matchDetailsUpdated,
}) => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmUpdate, setConfirmUpdate] = useState(false);
  const [updateType, setUpdateType] = useState();
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");

  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const toggleShowHistory = () => {
    setShowHistory(!showHistory);
  };

  const fetchHistory = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_CDD_NAME_UPDATE_HISTORY}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&kyc_id=${id ? id : ""}`,
        );
        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const historyData = data.data.data;
        setHistory(historyData);
        return data.data;
      } catch (error) {
        setHistory([]);
        setLoading(false);
      }
    },
    [id, limit, page],
  );

  useEffect(() => {
    if (showHistory) {
      fetchHistory();
    }
  }, [showHistory, fetchHistory]);

  /**
   * * Start: History List Change Limit
   * @param {Event} e (Limit Number)
   **/
  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchHistory(limit, 1);
  };

  //* End: History List Change Limit

  /**
   * * Start: History List Change Page
   * @param {Number} page (Page Number)
   **/
  const handleChangePage = (page) => {
    setPage(page);
    fetchHistory(limit, page);
  };

  const toggleConfirmUpdate = (type) => {
    if (type) {
      setUpdateType(type);
    }
    setConfirmUpdate(!confirmUpdate);
  };

  const toggleShowUpdate = () => {
    setShowUpdate((prev) => !prev);
  };

  const normalizeValue = (value) => {
    if (value === null || value === undefined) {
      return "";
    }

    return String(value).trim().toLowerCase();
  };

  const formatDate = (value) => {
    if (!value) return "";

    // Adjust this if your API uses a different date format.
    return value;
  };

  const compareFields = useMemo(() => {
    const fields = [
      {
        key: "name",
        label: "Name",
        contactValue: formatName(
          contactData?.first_name,
          contactData?.last_name,
        )
          ? formatName(contactData?.first_name, contactData?.last_name)
          : "",
        kycValue: kycData?.match_details?.name
          ? formatName(kycData?.first_name, kycData?.last_name)
          : formatName(contactData?.first_name, contactData?.last_name)
            ? formatName(contactData?.first_name, contactData?.last_name)
            : "",
      },
      {
        key: "dob",
        label: "Date of Birth",
        contactValue: formatDate(contactData?.dob),
        kycValue: formatDate(kycData?.dob ? kycData?.dob : contactData?.dob),
      },
      {
        key: "address",
        label: "Address",
        contactValue: formatFullAddressString(
          contactData?.street,
          contactData?.suburb,
          contactData?.state,
          contactData?.postal_code,
        )
          ? formatFullAddressString(
              contactData?.street,
              contactData?.suburb,
              contactData?.state,
              contactData?.postal_code,
            )
          : "",
        kycValue: kycData?.match_details?.address
          ? formatFullAddressString(
              kycData?.street,
              kycData?.suburb,
              kycData?.state,
              kycData?.postal_code,
            )
          : formatFullAddressString(
                contactData?.street,
                contactData?.suburb,
                contactData?.state,
                contactData?.postal_code,
              )
            ? formatFullAddressString(
                contactData?.street,
                contactData?.suburb,
                contactData?.state,
                contactData?.postal_code,
              )
            : "",
      },
    ];

    return fields.map((field) => ({
      ...field,
      isMatch:
        normalizeValue(field.contactValue) === normalizeValue(field.kycValue),
    }));
  }, [contactData, kycData]);

  const handleUpdate = async (field) => {
    try {
      setLoading(true);

      const formData = new FormData();

      if (field.key === "name") {
        formData.append("first_name", kycData?.first_name);
        formData.append("last_name", kycData?.last_name);
      } else if (field.key === "address") {
        formData.append("street", kycData?.street);
        formData.append("suburb_id", kycData?.suburb_id);
        formData.append("state_id", kycData?.state_id);
        formData.append("postal_code", kycData?.postal_code);
      } else {
        formData.append(`${field.key}`, field.kycValue);
      }
      formData.append("_method", "PUT");

      const response = await fileUpload(
        `${API.GET_CDD_NAME_UPDATE}${id ? `/${id}` : ""}`,
        formData,
      );

      if (response?.data) {
        setShowUpdate(false);
        toggleConfirmUpdate();
        fetchData && fetchData();
      }
    } catch (error) {
      console.error("Failed to update:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAll = async () => {
    try {
      setLoading(true);

      const fieldsToUpdate = compareFields
        .filter((field) => !field.isMatch)
        .map((field) => ({
          field: field.key,
          value: field.kycValue,
        }));

      const formData = new FormData();

      fieldsToUpdate.forEach((item) => {
        if (item.field === "name") {
          formData.append("first_name", kycData?.first_name);
          formData.append("middle_name", kycData?.middle_name);
          formData.append("last_name", kycData?.last_name);
        } else if (item.field === "address") {
          formData.append("street", kycData?.street);
          formData.append("suburb_id", kycData?.suburb_id);
          formData.append("state_id", kycData?.state_id);
          formData.append("postal_code", kycData?.postal_code);
        } else {
          formData.append(`${item.field}`, item.value);
        }
      });

      formData.append("_method", "PUT");

      const response = await fileUpload(
        `${API.GET_CDD_NAME_UPDATE}${id ? `/${id}` : ""}`,
        formData,
      );

      if (response?.data) {
        setShowUpdate(false);
        toggleConfirmUpdate();
        fetchData && fetchData();
      }
    } catch (error) {
      console.error("Failed to update all:", error);
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = compareFields.some((field) => !field.isMatch);

  const getChangedFields = (oldData = {}, newData = {}) => {
    const fields = new Set([...Object.keys(oldData), ...Object.keys(newData)]);

    return [...fields]
      .filter((field) => oldData?.[field] !== newData?.[field])
      .map((field) => ({
        field,
        oldValue: oldData?.[field],
        newValue: newData?.[field],
      }));
  };

  const formatFieldName = (field) => {
    return field
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const columns = [
    {
      dataField: "created_at",
      text: "Date & Time",
      headerClasses: THClasses,
      classes: TDClasses,
      formatter: (value) => (
        <div>
          <div>{moment(value).format("DD-MM-YYYY")}</div>
          <small className="text-muted-foreground">
            {moment(value).format("HH:mm:ss")}
          </small>
        </div>
      ),
    },
    {
      dataField: "user",
      text: "Changed By",
      headerClasses: THClasses,
      classes: TDClasses,
      formatter: (value) => formatSubcontractorName(value),
    },
    {
      dataField: "id",
      text: "Changes",
      headerClasses: THClasses,
      classes: TDClasses,
      formatter: (_, row) => {
        const changes = getChangedFields(row.old_data, row.new_data);

        return (
          <div className="flex flex-wrap gap-2">
            {changes.map((change) => (
              <Badge>
                {formatFieldName(change.field)}
              </Badge>
            ))}
          </div>
        );
      },
    },
  ];

  const HistoryDetails = ({ changes }) => {
    const columns = [
      {
        dataField: "field",
        text: "Field",
        headerClasses: THClasses,
        classes: TDClasses,
        formatter: (value) => (
          <span className="fw-semibold">{formatFieldName(value)}</span>
        ),
      },
      {
        dataField: "oldValue",
        text: "Previous Value",
        headerClasses: THClasses,
        classes: TDClasses,
        formatter: (value) => (
          <Badge variant={"destructive"}>{value || "-"}</Badge>
        ),
      },
      {
        dataField: "newValue",
        text: "New Value",
        headerClasses: THClasses,
        classes: TDClasses,
        formatter: (value) => <Badge variant={"success"}>{value || "-"}</Badge>,
      },
    ];

    return (
      <div className="custom-table p-2">
        <div className="table-body-class">
          <BootstrapTable
            keyField="field"
            remote
            data={changes}
            columns={columns}
            noDataIndication="No Data Found"
            classes={TableClasses}
            wrapperClasses={TableWrapClasses}
            rowClasses={TRClasses}
          />
        </div>
      </div>
    );
  };

  const expandRow = {
    renderer: (row) => {
      const changes = getChangedFields(row.old_data, row.new_data);

      return <HistoryDetails changes={changes} />;
    },
    onlyOneExpanding: true,
    showExpandColumn: true,
    expandByColumnOnly: true,
    headerClasses: THClasses,
    expandColumnRenderer: ({ expanded }) => {
      if (expanded) {
        return <CaretDownIcon />;
      }
      return <CaretRightIcon />;
    },
    className: TDClasses,
    expandHeaderColumnRenderer: () => {
      return <></>;
    },
  };

  return (
    <>
      <div className="flex items-center gap-2">
        {kycData?.match_details ? (
          <>
            <CustomTooltip
              message={
                <div>
                  <div className="mb-1">Id Document Data:</div>
                  <div className="mb-1">
                    Name:{" "}
                    {kycData?.match_details?.name
                      ? formatName(kycData?.first_name, kycData?.last_name)
                      : ""}
                  </div>
                  <div className="mb-1">
                    Date of Birth:{" "}
                    {formatDate(kycData?.dob ? kycData?.dob : "")}
                  </div>
                  <div>
                    Address:{" "}
                    {kycData?.match_details?.address
                      ? formatFullAddressString(
                          kycData?.street,
                          kycData?.suburb,
                          kycData?.state,
                          kycData?.postal_code,
                        )
                      : ""}
                  </div>
                </div>
              }
            >
              <Button className="w-fit p-1 h-fit">
                <InfoCircledIcon size={16} />
              </Button>
            </CustomTooltip>
            <CustomTooltip message={"Name Update"}>
              <Button
                className="w-fit p-1 h-fit"
                onClick={() => toggleShowUpdate()}
              >
                <UpdateIcon size={16} />
              </Button>
            </CustomTooltip>
          </>
        ) : (
          <></>
        )}

        {matchDetailsUpdated ? (
          <>
            <CustomTooltip message={"Record data updated using kyc data"}>
              <Button
                className="w-fit p-1 h-fit"
                onClick={() => toggleShowHistory()}
              >
                <History size={16} />
              </Button>
            </CustomTooltip>
          </>
        ) : (
          <></>
        )}
      </div>

      {showUpdate && (
        <ResponsiveDrawerForm
          description=""
          title="Update Changes"
          handleOpen={() => {}}
          handleClose={() => {
            toggleShowUpdate();
          }}
          write={true}
          buttonText=""
          className="sm:max-w-3xl"
          open={showUpdate}
          buttonClassName=""
          setOpen={setShowUpdate}
          popupType="cdds"
        >
          <div className="space-y-4">
            <div className="overflow-hidden rounded-md border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left font-medium">Field</th>

                    <th className="px-4 py-3 text-left font-medium">
                      Contact Data
                    </th>

                    <th className="px-4 py-3 text-left font-medium">
                      KYC Data
                    </th>

                    <th className="px-4 py-3 text-center font-medium">Match</th>

                    <th className="px-4 py-3 text-center font-medium">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {compareFields.map((field) => (
                    <tr key={field.key} className="border-b last:border-0">
                      <td className="px-4 py-3 font-medium">{field.label}</td>

                      <td className="px-4 py-3">{field.contactValue || "-"}</td>

                      <td className="px-4 py-3">{field.kycValue || "-"}</td>

                      <td className="px-4 py-3 text-center">
                        {field.isMatch ? (
                          <Check className="mx-auto h-5 w-5 text-green-600" />
                        ) : (
                          <X className="mx-auto h-5 w-5 text-red-600" />
                        )}
                      </td>

                      <td className="px-4 py-3 text-center">
                        {!field.isMatch && (
                          <Button
                            size="sm"
                            disabled={loading}
                            onClick={() => toggleConfirmUpdate(field)}
                          >
                            Update
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasChanges && (
              <div className="flex justify-end">
                <Button
                  disabled={loading}
                  onClick={() => toggleConfirmUpdate("all")}
                >
                  {loading ? "Updating..." : "Update All"}
                </Button>
              </div>
            )}
          </div>
        </ResponsiveDrawerForm>
      )}

      {confirmUpdate && (
        <Confirmation
          title={`Confirm!`}
          open={confirmUpdate}
          setOpen={setConfirmUpdate}
          toggleShow={() => {
            toggleConfirmUpdate();
          }}
          className="sm:max-w-1/2"
          message={
            <>
              <div>
                <p>
                  {updateType == "all"
                    ? "Are you sure you want to update all changed fields with the KYC data?"
                    : `Are you sure you want to update ${updateType?.label?.toLowerCase()}?`}
                </p>
              </div>
            </>
          }
          loading={loading}
          disabled={loading}
          okAction={async () => {
            if (updateType == "all") {
              handleUpdateAll();
            } else {
              handleUpdate(updateType);
            }
          }}
          cancelAction={() => {
            toggleConfirmUpdate();
          }}
        />
      )}

      {showHistory && (
        <ResponsiveDrawerForm
          description=""
          title="Update History"
          handleOpen={() => {}}
          handleClose={() => {
            toggleShowHistory();
          }}
          write={true}
          buttonText=""
          className="sm:max-w-3xl"
          open={showHistory}
          buttonClassName=""
          setOpen={setShowHistory}
          popupType="cdds"
        >
          {loading && <Spin/>}
          <div className="custom-table">
            <div className="table-body-class">
              <BootstrapTable
                keyField="id"
                remote
                data={history}
                columns={columns}
                expandRow={expandRow}
                noDataIndication="No Data Found"
                classes={TableClasses}
                wrapperClasses={TableWrapClasses}
                rowClasses={TRClasses}
              />
            </div>
            <Pagination
              total={total}
              limit={parseInt(limit)}
              currentPage={page}
              updateLimit={handleUpdateLimit}
              updatePage={handleChangePage}
              from={from}
              to={to}
            />
          </div>
        </ResponsiveDrawerForm>
      )}
    </>
  );
};

export default NameUpdate;
